import { GetPostPostCommentsQuery, GetPostsQuery } from "gql/graphql";
import { create } from "zustand";

type Post = GetPostsQuery["getPosts"]["content"][number];

type PostStorage = {
  posts: Post[];
  getSortedPosts: () => Post[];
  updateById: (id: string, post: Post) => void;
  pushPosts: (post: Post[]) => void;
  getPostById: (id: string) => Post;
  shiftPost: (post: Post) => void;
  clear: () => void;
  setPostComments: (
    id: string,
    comments?: GetPostPostCommentsQuery["getPostById"]["comments"],
  ) => void;
  removePost: (id: string) => void;
};

export const usePosts = create<PostStorage>((set, get) => ({
  posts: [],

  getSortedPosts: () => {
    return get().posts.sort((a, b) => {
      return (
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      );
    });
  },

  updateById: (id, post) =>
    set((state) => {
      const newPosts = [...state.posts];

      const index = newPosts.findIndex((post) => post.id === id);

      if (index !== -1) {
        newPosts[index] = post;
      }

      return {
        posts: newPosts,
      };
    }),

  pushPosts: (posts) =>
    set((state) => {
      const map = new Map();
      const newPosts: Post[] = [];

      function addIfNotExist(post: Post) {
        if (!map.has(post.id)) {
          newPosts.push(post);
          map.set(post.id, true);
        }
      }

      state.posts.forEach(addIfNotExist);
      posts.forEach(addIfNotExist);

      return {
        posts: newPosts,
      };
    }),

  shiftPost: (post) =>
    set((state) => {
      return {
        posts: [post, ...state.posts],
      };
    }),

  getPostById: (id) => {
    const posts = get().posts;
    const result = posts.find((post) => post.id === id) as Post;

    return result;
  },

  clear: () => {
    set(() => ({ posts: [] }));
  },

  setPostComments: (id, comments = []) => {
    set((state) => ({
      posts: state.posts.map((post) =>
        post.id === id ? { ...post, comments: comments } : post,
      ),
    }));
  },

  removePost: (id) => {
    set((state) => ({
      posts: state.posts.filter((post) => post.id !== id),
    }));
  },
}));
